import { queryHelpers, useMediaQuery } from '@leagueplatform/genesis-core';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * A custom hook that calculates and manages the height of a header.
 *
 * This hook handles dynamic height adjustments based on content size and viewport changes.
 * It returns the calculated height values and a ref to attach to the content element.
 *
 * @returns An object containing:
 * - `height` - The calculated height of the header content (with partial padding) as a CSS pixel value
 * - `pageHeaderBackgroundHeight` - The calculated total height of the header (with full padding) as a CSS pixel value
 * - `contentRef` - A React ref to attach to the header content element
 */

export const useDynamicHeaderHeight = () => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [contentHeight, setContentHeight] = useState(50);
  const isLaptopDown = useMediaQuery(queryHelpers.down('laptop'));
  const pageHeaderBackgroundHeightPadding = isLaptopDown ? 80 : 112;

  useLayoutEffect(() => {
    const reportWindowSize = () => {
      setContentHeight(contentRef?.current?.clientHeight ?? contentHeight);
    };
    if (contentHeight !== undefined)
      window.addEventListener('resize', reportWindowSize);
    return () => window.removeEventListener('resize', reportWindowSize);
  }, [contentHeight, contentRef]);

  useEffect(() => {
    if (contentRef?.current?.clientHeight)
      setContentHeight(contentRef.current.clientHeight);
  }, [contentRef]);

  const height = `${contentHeight + pageHeaderBackgroundHeightPadding / 2}px`;
  const pageHeaderBackgroundHeight = `${
    contentHeight + pageHeaderBackgroundHeightPadding
  }px`;

  return { height, pageHeaderBackgroundHeight, contentRef };
};
